import Table from "../Table/Table";
import React, { useEffect, useState, useCallback } from "react";
import ContactBlock from "../ContentBlock/ContactBlock";
import FilterPanel from "../Filters/FilterPanel";
import { useFilterParams } from "../hooks/useFilterParams";
import axios from "axios";
import { useGlobalState } from "../GlobalContext";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { apiLabelUrl } from "../Functions/functions";

// Status options for quotations
const QUOTATION_STATUS_OPTIONS = [
    { value: "offerte", label: "Quotation" },
    { value: "aanvraag", label: "Request" },
];

export default function QuotationOverview(props) {
    const { loggedInUser } = useGlobalState();
    const { textData, responsive } = props;
    const [loading, setLoading] = useState(true);
    const [quotationData, setQuotationData] = useState([]);
    const [openPopup, setOpenPopup] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [groupedData, setGroupedData] = useState(null);

    // Initialize filters from URL
    const { filters, updateFilter, resetFilters, getApiParams } = useFilterParams({
        status: null,
        date_from: null,
        date_to: null,
        price_min: null,
        price_max: null,
        sort_by: null,
        sort_direction: null,
        group_by: null,
        search: null,
    });

    useEffect(() => {
        // Check in url if a message is given
        let urlParams = new URLSearchParams(window.location.search);
        let message = urlParams.get("message");
        if (typeof message !== "undefined" && message !== null) {
            // Show notification
            message = atob(message);
            toast.error(message);
        }
    }, []);

    // Fetch quotations when page or filters change
    useEffect(() => {
        debouncedGetQuotations(currentPage);
    }, [currentPage, filters]);

    useEffect(() => {
        loadSelectedQuotation();
    }, [quotationData]);

    const loadSelectedQuotation = () => {
        if (openPopup !== null) return;
        if (typeof quotationData[0]?.orders !== "undefined" && quotationData[0]?.orders.length !== 0) {
            // get id from url param
            let urlParams = new URLSearchParams(window.location.search);
            let id = urlParams.get("id");

            if (id !== null) {
                for (let orderId in quotationData[0]?.orders) {
                    if (quotationData[0]?.orders[orderId].project_number == id) {
                        setOpenPopup({ id: quotationData[0]?.orders[orderId].project_number, type: "quotation" });
                        break;
                    }
                }
            }
        }
    };

    const fetchLoginData = async () => {
        try {
            const response = await axios.get((await apiLabelUrl("web")) + "/currentuser");
            return new Promise((resolve, reject) => {
                resolve(response.data);
            });
        } catch (error) {
            console.error(error);
        }
    };

    const getQuotations = (page) => {
        if (typeof loggedInUser !== "undefined") {
            getQuotationData(loggedInUser, page);
        } else {
            fetchLoginData().then((user) => {
                getQuotationData(user, page);
            });
        }
    };

    const getQuotationData = async (user, page) => {
        setLoading(true);
        const quotationAPI = (await apiLabelUrl("api")) + "/quotations";

        // Build params from filters
        const filterParams = getApiParams();

        axios
            .get(quotationAPI, {
                params: {
                    user_id: user.id,
                    page: page,
                    per_page: 5,
                    ...filterParams,
                },
            })
            .then((response) => {
                // Check if response is grouped
                if (response.data.grouped) {
                    setGroupedData(response.data);
                    setQuotationData([]);
                    setTotalPages(1);
                } else {
                    setGroupedData(null);
                    let result = response.data;
                    result.section = "savedQuotation";
                    setQuotationData([result]);
                    setTotalPages(response.data.total_pages);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                setGroupedData(null);
                setQuotationData([]);
            });
    };

    // Create a debounced version of the getQuotations function
    const debouncedGetQuotations = useCallback(
        debounce((page) => getQuotations(page), 500),
        [filters]
    );

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Handle filter apply (for mobile)
    const handleFilterApply = () => {
        setCurrentPage(1);
        getQuotations(1);
    };

    // Get translated status options
    const getStatusOptions = () => {
        return QUOTATION_STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: textData?.["filters"]?.[`status_${opt.value}`] || opt.label,
        }));
    };

    return (
        <>
            {/* Filter Panel */}
            <div className="quotations-filter-section">
                <FilterPanel
                    filters={filters}
                    updateFilter={updateFilter}
                    resetFilters={resetFilters}
                    onApply={handleFilterApply}
                    textData={textData}
                    statusOptions={getStatusOptions()}
                    responsive={responsive}
                />
            </div>

            {/* Grouped Data Display */}
            {groupedData && groupedData.groups && groupedData.groups.length > 0 && (
                <>
                    <h1 style={responsive ? { margin: "0 10px" } : {}}>
                        {textData?.["account/quotations"]?.["table"]?.["title"] || "Quotations"}({groupedData.total})
                    </h1>
                    <Table
                        responsive={responsive}
                        groupedData={groupedData}
                        textData={!!textData && textData["account/quotations"]}
                        textDataLabel={!!textData && textData["sections/label"]}
                        navigateToPageName={props.navigateToPageName}
                        allTextData={!!textData && textData}
                        openPopup={openPopup}
                        loading={loading}
                    />
                </>
            )}

            {/* Regular Data Display */}
            {!groupedData &&
                typeof quotationData[0]?.orders !== "undefined" &&
                quotationData[0]?.orders.length !== 0 && (
                    <>
                        <h1 style={responsive ? { margin: "0 10px" } : {}}>
                            {textData["account/quotations"]["table"]["title"]}
                        </h1>
                        <Table
                            responsive={responsive}
                            data={quotationData}
                            textData={!!textData && textData["account/quotations"]}
                            textDataLabel={!!textData && textData["sections/label"]}
                            navigateToPageName={props.navigateToPageName}
                            allTextData={!!textData && textData}
                            openPopup={openPopup}
                            loading={loading}
                        />
                        <div className="pagination-controls">
                            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                            <span>
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                    </>
                )}

            {/* Loading State */}
            {loading && quotationData.length === 0 && !groupedData && (
                <div className="modelviewer-loading centered">
                    <span className="spinner"></span>
                </div>
            )}

            {/* Contact Block for Mobile */}
            {!!responsive && !!textData && !!textData["content_blocks/contact"] && (
                <ContactBlock responsive={responsive} textData={textData["content_blocks/contact"]} />
            )}

            {/* No Data Message */}
            {!groupedData &&
                (typeof quotationData[0]?.orders === "undefined" || quotationData[0]?.orders.length === 0) &&
                !loading && <h2>{!!textData && textData["account/quotations"]["table"]["no_data"]}</h2>}
        </>
    );
}
